import React, { forwardRef } from 'react'

import { Input as AntdInput } from 'antd'
import styled, { css } from 'styled-components'
import type { TextAreaProps } from 'rc-textarea/lib/interface'
import { TextAreaRef } from 'antd/es/input/TextArea'

import { variables } from 'src/Styled/variables'

const { TextArea: AntdTextArea } = AntdInput

const commonStyles = css`
  font-size: ${variables.fontSize12};
  opacity: 1;
  top: 7px;
  left: 16px;
  transition: all 0.2s ease;
  margin: 0;
  z-index: 2;
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;

  &:focus-within p {
    ${commonStyles}
  }

  &:has(textarea:not(:empty)) p {
    ${commonStyles}
  }

  &:has(textarea:not(:empty)) span {
    padding-top: 27px !important;
  }

  &:has(textarea:disabled) p {
    color: ${({ theme }) => theme.color.lightGrey};
  }
`

const Label = styled.p`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.slateGrey};
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  opacity: 0;
  transition: all 0.2s ease;
  position: absolute;
  top: 27px;
  left: 18px;
  z-index: 0;
`

const ErrorMessage = styled.div`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.mediumRed};
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  margin-top: 5px;
`

const WarningMessage = styled(ErrorMessage)`
  color: ${({ theme }) => theme.color.mediumOrange};
`

const StyledTextArea = styled(AntdTextArea)<{
  $isError?: boolean
  $isWarning?: boolean
}>`
  color: ${({ theme }) => theme.color.darkGrey};
  background-color: ${({ theme }) => theme.color.white} !important;
  border-radius: 16px;
  padding: 16px !important;
  padding-top: 24px !important;

  border: ${({ theme, $isError, $isWarning }) => {
    if ($isError) {
      return `1px solid ${theme.color.mediumRed}`
    }
    if ($isWarning) {
      return `1px solid ${theme.color.mediumOrange}`
    }
    return `1px solid ${theme.color.lightGrey}`
  }} !important;
  position: relative;

  .ant-input {
    padding: 0;
    background-color: ${({ theme }) => theme.color.white} !important;
    color: ${({ theme }) => theme.color.darkGrey};
  }

  &.ant-input-outlined {
    box-shadow: none;
  }

  &:not([value='']) {
    padding-top: 17px !important;
    padding-bottom: 7px !important;
  }

  ::placeholder {
    color: ${({ theme }) => theme.color.slateGrey};
    font-family: 'Segoe UI';
    line-height: 150%;
    font-size: ${variables.fontSize14};
    font-weight: ${variables.fontWeight400};
    transition: all 0.2s ease;
  }

  &.ant-input-affix-wrapper-focused {
    background-color: ${({ theme }) => theme.color.white} !important;
    border: ${({ theme, $isError, $isWarning }) => {
      if ($isError) {
        return `1px solid ${theme.color.mediumRed}`
      }
      if ($isWarning) {
        return `1px solid ${theme.color.mediumOrange}`
      }
      return `1px solid ${theme.color.brandGreen}`
    }} !important;

    padding-top: 27px !important;

    textarea {
      background-color: ${({ theme }) => theme.color.white} !important;
      &::placeholder {
        opacity: 0;
        transition: all 0.2s ease;
      }
    }
  }

  .ant-input-data-count {
    color: ${({ theme }) => theme.color.darkGrey};
    font-family: 'Segoe UI';
    line-height: 150%;
    font-size: ${variables.fontSize12};
    font-weight: ${variables.fontWeight400};
  }

  &:hover:not(.ant-input-affix-wrapper-disabled) {
    background-color: ${({ theme }) => theme.color.hoverWhite} !important;

    textarea {
      background-color: ${({ theme }) => theme.color.hoverWhite} !important;
    }
  }

  &.ant-input-affix-wrapper-disabled {
    color: ${({ theme }) => theme.color.darkGrey};
    border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`} !important;
    background-color: ${({ theme }) => theme.color.white};

    textarea {
      color: ${({ theme }) => theme.color.darkGrey};

      &::placeholder {
        color: ${({ theme }) => theme.color.lightGrey};
      }
    }

    &:hover {
      background-color: ${({ theme }) => theme.color.white};
    }
  }

  &.ant-input-affix-wrapper-disabled:hover {
    background-color: ${({ theme }) => theme.color.white} !important;
  }

  textarea.ant-input-disabled {
    color: ${({ theme }) => theme.color.lightGrey};
  }

  &&:active {
    background-color: ${({ theme }) => theme.color.white} !important;
  }

  textarea {
    background-color: ${({ theme }) => theme.color.white} !important;
    color: ${({ theme }) => theme.color.darkGrey};
  }
`

export type TextareaProps = TextAreaProps & {
  error?: string
  warning?: string
}

export const PrimaryTextArea = forwardRef<TextAreaRef, TextareaProps>(
  (props, ref) => {
    const { placeholder, error, warning } = props

    return (
      <Wrapper>
        <Label>{placeholder}</Label>
        <StyledTextArea
          {...props}
          $isError={Boolean(error)}
          $isWarning={Boolean(warning)}
          ref={ref}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {warning && <WarningMessage>{warning}</WarningMessage>}
      </Wrapper>
    )
  },
)

PrimaryTextArea.displayName = 'PrimaryTextArea'
